import React, { useState } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  IconButton,
  Pagination,
  Stack,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import CloseIcon from '@mui/icons-material/Close';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SMSTemplate = () => {
  const theme = useTheme();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [smsFor, setSmsFor] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [smsContent, setSmsContent] = useState('');
  const [enableTemplate, setEnableTemplate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Static data for SMS For dropdown
  const staticSmsForData = [
    { id: 1, name: 'Registration' },
    { id: 2, name: 'Admission' },
    { id: 3, name: 'Enquiry' },
  ];

  // Static data for saved SMS templates
  const [savedTemplates, setSavedTemplates] = useState([
    { id: 1, smsFor: 'Registration', templateId: 'REG001', smsContent: 'Welcome! Your registration is successful.', enableTemplate: true },
    { id: 2, smsFor: 'Admission', templateId: 'ADM001', smsContent: 'Your admission is confirmed.', enableTemplate: false },
  ]);

  const handleSave = (e) => {
    e.preventDefault();
    if (smsFor && templateId && smsContent) {
      const newTemplate = {
        id: savedTemplates.length + 1,
        smsFor: staticSmsForData.find(item => item.id === smsFor)?.name || '',
        templateId,
        smsContent,
        enableTemplate,
      };
      setSavedTemplates([...savedTemplates, newTemplate]);
       setShowSuccessModal(true)
    } else {
      setSnackbarMessage('Please fill all required fields');
      setOpenSnackbar(true);
      setSnackbarSeverity('error')
    }
  };

  const handleReset = () => {
    setSmsFor('');
    setTemplateId('');
    setSmsContent('');
    setEnableTemplate(false);
  };

  const handleDelete = (id) => {
    setSavedTemplates(savedTemplates.filter(template => template.id !== id));
  };

  const handleEdit = (template) => {
    setSmsFor(staticSmsForData.find(item => item.name === template.smsFor)?.id || '');
    setTemplateId(template.templateId);
    setSmsContent(template.smsContent);
    setEnableTemplate(template.enableTemplate);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const filteredTemplates = savedTemplates.filter(template =>
    template.smsFor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.templateId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.smsContent.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedTemplates = filteredTemplates.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const isFormFilled = smsFor && templateId && smsContent;

  return (
    <Container sx={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* Form Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:'flex-end',
          mb:1
        }}
      >
        <IconButton
          onClick={() => setShowUserGuideModal(true)}
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            minWidth: 0, // Remove MUI's default padding
          }}
        >
          <InfoOutlineIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Box>
      <Box
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
              }}
            >
        <FormControl fullWidth sx={{ my: 2 }}>
          <InputLabel id="sms-for-label" sx={{ color: theme.palette.text.primary }}>
            SMS For
          </InputLabel>
          <Select
            labelId="sms-for-label"
            id="sms-for"
            value={smsFor}
            label="SMS For"
            onChange={(e) => setSmsFor(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select SMS type</em>
            </MenuItem>
            {staticSmsForData.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Template ID"
          variant="outlined"
          fullWidth
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          sx={{
            my: 2,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            },
          }}
        />
        <TextField
          label="SMS Content"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={smsContent}
          onChange={(e) => setSmsContent(e.target.value)}
          sx={{
            my: 2,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            },
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={enableTemplate}
              onChange={(e) => setEnableTemplate(e.target.checked)}
              color="primary"
              sx={{
                        color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
                        '&.Mui-checked': { color: theme.palette.primary.main },
                      }}
            />
          }
          label="Enable Template"
          sx={{ my: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1}}>
          <Tooltip
            title={
              !smsFor ? "Select SMS For" :
              !templateId ? "Enter Template ID" :
              !smsContent ? "Enter SMS Content" : ""
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  mr: 2,
                  width: '100px',
                  height: '50px',
                  borderRadius: '10px',
                  padding: '10px 0',
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
                disabled={!isFormFilled}
              >
                SAVE
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleReset}
            sx={{
              width: '100px',
              height: '50px',
              borderRadius: '10px',
              padding: '10px 0',
              fontWeight: 'bold',
            }}
          >
            RESET
          </Button>
        </Box>
      </Box>

      {/* Table Section */}
     
         
      <Box sx={{ display: 'flex', justifyContent:'space-between', alignItems: 'center', mb: 1, p: 2, boxShadow: 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
         <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 2, }}>
        Saved SMS Template
      </Typography> 
      {/* <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300, maxWidth: '100%' }}
        /> */}
      </Box>
      <Paper elevation={3} sx={{ paddingBottom: 0, maxWidth: '100%', boxSizing: 'border-box', mx: 'auto', backgroundColor: theme.palette.background.paper, borderRadius: '16px' }}>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white'
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>SMS For</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Template ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>SMS Content</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTemplates.map((template, index) => (
                  <TableRow
                    key={template.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{template.smsFor}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{template.templateId}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{template.smsContent}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>
                      <Checkbox
                        checked={template.enableTemplate}
                        sx={{
                        color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
                        '&.Mui-checked': { color: theme.palette.primary.main },
                      }}
                        onChange={(e) => {
                          const updatedTemplates = savedTemplates.map(t =>
                            t.id === template.id ? { ...t, enableTemplate: e.target.checked } : t
                          );
                          setSavedTemplates(updatedTemplates);
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEdit(template)}
                        sx={{
                          color: theme.palette.primary.light,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(template.id)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Pagination */}
      {filteredTemplates.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography
            variant="body2"
            color={theme.palette.primary.main}
          >
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredTemplates.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredTemplates.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredTemplates.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={(e) => {
                  const newRowsPerPage = Number(e.target.value);
                  setRowsPerPage(newRowsPerPage);
                  handleChangePage(null, 1);
                }}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Template Saved ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              SMS Template Saved Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  setSmsFor('');
                  setTemplateId('');
                  setSmsContent('');
                  setEnableTemplate(false);
                }}
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: '10px',
                  height: '40px',
                  padding: '0 20px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for errors */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
       <Modal
      open={showUserGuideModal}
      onClose={() => setShowUserGuideModal(false)}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
            SMS Template - User Guide
          </Typography>
          <IconButton
            onClick={() => setShowUserGuideModal(false)}
            sx={{ color: '#fff', position: 'absolute', right: 14 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
          {/* Section 1: Save SMS Template */}
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
          >
            ➤ Follow these steps to save an SMS template:
          </Typography>
          <ul
            style={{
              marginLeft: '20px',
              lineHeight: '1.8',
              cursor: 'pointer',
            }}
          >
            <li>
              Select an <strong>SMS For</strong> type from the dropdown menu (e.g., Registration, Admission, Enquiry).
            </li>
            <li>
              Enter a unique <strong>Template ID</strong> in the provided text field.
            </li>
            <li>
              Enter the <strong>SMS Content</strong> in the multiline text field.
            </li>
            <li>
              Optionally, check the <strong>Enable Template</strong> box to activate the template.
            </li>
            <li>
              Review all entered information to ensure accuracy.
            </li>
            <li>
              Click the <strong>"SAVE"</strong> button to save the template. A success message will confirm the action and clear the form.
            </li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
            ⚠️ Note: SMS For, Template ID, and SMS Content are required fields.
          </Typography>

          {/* Section 2: Reset Form */}
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
          >
            ➤ Follow these steps to reset the form:
          </Typography>
          <ul
            style={{
              marginLeft: '20px',
              lineHeight: '1.8',
              cursor: 'pointer',
            }}
          >
            <li>
              Click the <strong>"RESET"</strong> button to clear all form fields (SMS For, Template ID, SMS Content, Enable Template).
            </li>
            <li>
              This will restore the form to its initial state.
            </li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
            ⚠️ Note: Resetting the form does not save any entered data.
          </Typography>

          {/* Section 3: Edit SMS Template */}
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
          >
            ➤ Follow these steps to update an SMS template:
          </Typography>
          <ul
            style={{
              marginLeft: '20px',
              lineHeight: '1.8',
              cursor: 'pointer',
            }}
          >
            <li>
              In the Saved SMS Template table, select the template whose ID you want to edit/update.
            </li>
            <li>
              Then click the <strong>Edit</strong> icon (pencil) button in the Action column for the desired template.
            </li>
            <li>
              The form will populate with the template’s details (SMS For, Template ID, SMS Content, Enable Template).
            </li>
            <li>
              Before clicking on <strong>"SAVE"</strong> button, review the necessary changes to the form fields.
            </li>
            <li>
              Then click the <strong>"SAVE"</strong> button to save the updated template.
            </li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
            ⚠️ Note: Editing a template will create a new entry in the table. Ensure all required fields are filled before saving.
          </Typography>

          {/* Section 4: Delete SMS Template */}
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
          >
            ➤ Follow these steps to delete an SMS template:
          </Typography>
          <ul
            style={{
              marginLeft: '20px',
              lineHeight: '1.8',
              cursor: 'pointer',
            }}
          >
            <li>
              In the Saved SMS Template table, choose the template whose ID you want to delete.
            </li>
            <li>
              Then click the <strong>Delete</strong> icon button(trash can) in the Action column to delete the desired template record.
            </li>
            <li>
              The template record will be removed from the table immediately.
            </li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
            ⚠️ Note: Deleting a template is permanent and cannot be undone.
          </Typography>

          {/* Section 5: Toggle Template Status */}
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
          >
            ➤ Follow these steps to toggle/enable template status:
          </Typography>
          <ul
            style={{
              marginLeft: '20px',
              lineHeight: '1.8',
              cursor: 'pointer',
            }}
          >
            <li>
              In the Saved SMS Template table, choose the template whose ID you want to enable or disable.
            </li>
            <li>
              Then check or uncheck the <strong>Status</strong> checkbox in the Status column to enable or disable the template.
            </li>
            <li>
              The template’s status will update immediately in the table.
            </li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
            ⚠️ Note: Enabling a template makes it active for use; disabling it prevents its use without deleting it.
          </Typography>

          {/* Section 6: Pagination */}
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
          >
            ➤ Follow these steps to navigate the saved SMS templates list:
          </Typography>
          <ul
            style={{
              marginLeft: '20px',
              lineHeight: '1.8',
              cursor: 'pointer',
            }}
          >
            <li>
              Use the pagination controls at the bottom to navigate through pages of saved SMS templates.
            </li>
            <li>
              Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to move between pages.
            </li>
            <li>
              Select a specific page number by clicking on it.
            </li>
            <li>
              Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 20, 30 rows).
            </li>
          </ul>
          <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
            ⚠️ Note: The table displays only the selected number of rows per page.
          </Typography>

          {/* Bottom Close Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            {/* <Button
              variant="contained"
              onClick={() => setShowUserGuideModal(false)}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                fontWeight: 'bold',
                padding: '8px 24px',
                borderRadius: '8px',
              }}
            >
              Close
            </Button> */}
          </Box>
        </CustomTableScrollbar>
      </Box>
    </Modal>
    </Container>
  );
};

export default SMSTemplate;
